/**
 * Blueprint plumbing. design.md section 8.
 *
 * The page blueprints rearrange the migrated blocks into design slots. That
 * rearrangement is only allowed to move content, never to lose it, so every
 * helper here reads from the content layer and nothing here ever holds a
 * migrated string of its own.
 *
 * Two mechanisms carry the guarantee:
 *
 *   `Ledger`  a blueprint claims the blocks it places by hand; whatever it did
 *             not claim comes back from `rest()` and is rendered by the generic
 *             block stream in a fallback slot. A blueprint can therefore never
 *             silently drop a block, not even when the data changes under it.
 *
 *   `splitTopLevel`  an `html` block is split into its top-level elements so a
 *             blueprint can put the intro paragraph in one slot and the list in
 *             another. The parts are the source substrings, untouched.
 */
import type { Block, HeadingBlockData, HtmlBlockData, PageMeta } from './content';
import { getPage, loadBlocks, plainText } from './content';
import { headingShape } from './headings';
import { slugify } from './slug';

/* -------------------------------------------------------------------------- */
/* Splitting an html block                                                    */
/* -------------------------------------------------------------------------- */

export type Part = {
  /** The element's tag, or null for loose text and inline markup. */
  tag: string | null;
  /** Inner html (for a loose part, the text itself). */
  html: string;
  /** The source substring, tags included. */
  outer: string;
};

const BLOCK_TAGS = new Set([
  'p', 'div', 'ul', 'ol', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'table', 'blockquote', 'figure', 'pre', 'section', 'hr', 'dl', 'iframe',
]);
const VOID_TAGS = new Set(['br', 'hr', 'img', 'input', 'meta', 'link', 'source', 'wbr']);
const TAG_RE = /<!--[\s\S]*?-->|<\/?([a-zA-Z][a-zA-Z0-9-]*)((?:"[^"]*"|'[^']*'|[^>])*)>/g;

/**
 * The top-level elements of a fragment, in source order.
 *
 * WordPress leaves malformed markup behind (a stray `</p>`, a paragraph that
 * is never opened, five paragraphs separated by blank lines and no tags at
 * all). Loose text is returned as its own part, split on blank lines, so a
 * blueprint can wrap it; a stray closing tag is dropped, because a tag is not
 * a word and the fidelity check counts words.
 */
export function splitTopLevel(html: string): Part[] {
  const parts: Part[] = [];
  let buffer = '';
  let depth = 0;
  let start = -1;
  let openTag = '';
  let last = 0;

  const flush = () => {
    const trimmed = buffer.trim();
    buffer = '';
    if (!trimmed) return;
    for (const chunk of trimmed.split(/\n[ \t]*\n+/)) {
      const c = chunk.trim();
      if (c) parts.push({ tag: null, html: c, outer: c });
    }
  };

  for (const m of html.matchAll(TAG_RE)) {
    const index = m.index ?? 0;
    const raw = m[0];
    if (raw.startsWith('<!--')) {
      if (depth === 0) {
        buffer += html.slice(last, index);
        last = index + raw.length;
      }
      continue;
    }
    const tag = (m[1] ?? '').toLowerCase();
    const closing = raw.startsWith('</');
    const selfClosing = /\/>$/.test(raw) || VOID_TAGS.has(tag);

    if (depth === 0) {
      if (!BLOCK_TAGS.has(tag)) continue; // inline markup stays in the buffer
      buffer += html.slice(last, index);
      if (closing) {
        // A close with no open: WordPress residue. Drop the tag, keep the text.
        last = index + raw.length;
        continue;
      }
      flush();
      if (selfClosing) {
        parts.push({ tag, html: '', outer: raw });
        last = index + raw.length;
        continue;
      }
      depth = 1;
      start = index;
      openTag = tag;
      last = index + raw.length;
      continue;
    }

    if (tag === openTag && !selfClosing) depth += closing ? -1 : 1;
    if (depth === 0) {
      const end = index + raw.length;
      const outer = html.slice(start, end);
      parts.push({ tag: openTag, html: innerOf(outer), outer });
      last = end;
    }
  }

  if (depth > 0) {
    const outer = html.slice(start);
    parts.push({ tag: openTag, html: innerOf(outer), outer });
  } else {
    buffer += html.slice(last);
    flush();
  }
  return parts;
}

/**
 * The content of a block element, without its own wrapper tags.
 *
 * The trailing close tag is only removed when it is the wrapper's own. Source
 * blocks are not always balanced: /screenings ends on `<p><strong>…</strong>`
 * with no `</p>`, and stripping "the last close tag" there would eat the
 * `</strong>` and hand back inline markup that no longer closes. A browser
 * parsing that in a prerendered document reopens the `<strong>` over
 * everything that follows, which is both wrong and a hydration mismatch.
 */
function innerOf(outer: string): string {
  const open = /^<([a-zA-Z][a-zA-Z0-9-]*)(?:"[^"]*"|'[^']*'|[^>])*>/.exec(outer);
  if (!open) return outer;
  const inner = outer.slice(open[0].length);
  const close = new RegExp(`</${open[1]}\\s*>\\s*$`, 'i');
  return close.test(inner) ? inner.replace(close, '') : inner;
}

/** Inner html of every `<li>` in a fragment, in order. */
export function listItems(html: string): string[] {
  return [...html.matchAll(/<li\b[^>]*>([\s\S]*?)<\/li>/gi)].map((m) => m[1].trim());
}

/** The first `<a href>` in a fragment, or null. */
export function firstHref(html: string): string | null {
  const m = /<a\b[^>]*\bhref\s*=\s*"([^"]*)"/i.exec(html);
  return m ? m[1] : null;
}

/** A fragment with its outermost anchor unwrapped, so it can be re-linked. */
export function stripAnchors(html: string): string {
  return html.replace(/<\/?a\b[^>]*>/gi, '');
}

/** The inline content of a `heading` block: the words without their wrapper. */
export function headingInner(block: HeadingBlockData): string {
  const shape = headingShape(block);
  return shape.kind === 'mixed' ? shape.inner : shape.inner;
}

/** Plain text of any block-ish value, for aria labels and ids only. */
export function textOf(input: string): string {
  return plainText(input);
}

/** A stable fragment id for a heading, used by the article table of contents. */
export function headingId(text: string): string {
  return slugify(text) || 'abschnitt';
}

/**
 * Give every `h2`/`h3` in a rendered fragment an `id`, and report them, so the
 * sidebar table of contents and the headings agree without a DOM walk.
 */
export function withHeadingIds(html: string): {
  html: string;
  headings: { id: string; level: number; text: string }[];
} {
  const headings: { id: string; level: number; text: string }[] = [];
  const seen = new Set<string>();
  const out = html.replace(
    /<h([23])((?:"[^"]*"|'[^']*'|[^>])*)>([\s\S]*?)<\/h\1>/gi,
    (match, digit: string, attrs: string, inner: string) => {
      const text = plainText(inner);
      if (!text) return match;
      if (/\bid\s*=/.test(attrs)) return match;
      let id = headingId(text);
      let n = 2;
      while (seen.has(id)) id = `${headingId(text)}-${n++}`;
      seen.add(id);
      headings.push({ id, level: Number(digit), text });
      return `<h${digit}${attrs} id="${id}">${inner}</h${digit}>`;
    },
  );
  return { html: out, headings };
}

/* -------------------------------------------------------------------------- */
/* The ledger                                                                 */
/* -------------------------------------------------------------------------- */

export type Claimed<T extends Block = Block> = T | undefined;

/**
 * Tracks which of a page's blocks a blueprint has placed.
 *
 * Claiming is by index, because the blueprints in design.md 8 are written
 * against the source order. `rest()` returns everything still unplaced, in
 * source order, which the blueprint renders through the generic stream. That
 * is what makes a blueprint safe: a block the layout has no slot for still
 * reaches the DOM.
 */
export class Ledger {
  readonly blocks: Block[];
  private readonly used = new Set<number>();

  constructor(blocks: Block[]) {
    this.blocks = blocks;
  }

  /** The block at `index`, claimed. */
  at<T extends Block = Block>(index: number, type?: T['type']): Claimed<T> {
    const block = this.blocks[index];
    if (!block) return undefined;
    if (type && block.type !== type) return undefined;
    this.used.add(index);
    return block as T;
  }

  /** The block at `index` without claiming it. */
  peek(index: number): Block | undefined {
    return this.blocks[index];
  }

  /** The first block of `type` at or after `from`, claimed. */
  first<T extends Block = Block>(type: T['type'], from = 0): Claimed<T> {
    for (let i = from; i < this.blocks.length; i += 1) {
      if (this.blocks[i].type === type && !this.used.has(i)) {
        this.used.add(i);
        return this.blocks[i] as T;
      }
    }
    return undefined;
  }

  /** Every block of `type`, claimed, in source order. */
  all<T extends Block = Block>(type: T['type']): T[] {
    const out: T[] = [];
    this.blocks.forEach((block, i) => {
      if (block.type !== type) return;
      this.used.add(i);
      out.push(block as T);
    });
    return out;
  }

  /** Claim a range without returning it (used for blocks placed as a group). */
  claim(...indices: number[]): void {
    for (const i of indices) if (this.blocks[i]) this.used.add(i);
  }

  /** Everything not yet claimed, in source order. */
  rest(): Block[] {
    return this.blocks.filter((_, i) => !this.used.has(i));
  }

  /** Unclaimed blocks that carry words. Separators and empty images do not. */
  restWithContent(): Block[] {
    return this.rest().filter((b) => hasWords(b));
  }
}

/** Whether a block contributes any word to the fidelity check. */
export function hasWords(block: Block): boolean {
  switch (block.type) {
    case 'html':
      return plainText(block.html).length > 0;
    case 'heading':
    case 'section_title':
      return plainText((block as HeadingBlockData).html ?? '').length > 0;
    case 'button':
      return Boolean(block.text?.trim());
    case 'counter':
      return true;
    case 'accordion_item':
      return true;
    default:
      return false;
  }
}

/* -------------------------------------------------------------------------- */
/* Cross-page content                                                         */
/* -------------------------------------------------------------------------- */

/**
 * The blocks of another page, as a promise for `use()`.
 *
 * design.md 8.1 section 7 needs the six data-protection commitments on the
 * homepage and says they are read from the `/datensicherheit` page's own list
 * block and never retyped. This is how that read happens: the same per-page
 * chunk the route itself would load, so the strings have exactly one origin.
 */
export function otherPage(route: string): PageMeta | undefined {
  return getPage(route);
}

export function blocksOf(route: string): Promise<Block[]> {
  const page = getPage(route);
  return page ? loadBlocks(page.id) : Promise.resolve<Block[]>([]);
}

/** The `<li>` strings of the first list in a page's first `html` block. */
export function listOfHtmlBlock(blocks: Block[]): string[] {
  for (const block of blocks) {
    if (block.type !== 'html') continue;
    const items = listItems((block as HtmlBlockData).html);
    if (items.length) return items;
  }
  return [];
}

/** A two-digit index for the ruled commitment lists ("01" … "06"). */
export function twoDigit(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/* -------------------------------------------------------------------------- */
/* The site's one contact block                                               */
/* -------------------------------------------------------------------------- */

export type ContactBlock = {
  /** The `heading` block that titles it. */
  title?: HeadingBlockData;
  /** The display paragraph under the title. */
  text?: HeadingBlockData;
  /** WhatsApp, E-Mail, Telefon, in source order. */
  buttons: Block[];
  /** Source indices, so a blueprint can claim them. */
  indices: number[];
};

/**
 * The homepage's mid-page contact block, located by shape rather than by copy.
 *
 * design.md 5.16 makes this block the content of the CTA band on every page,
 * so it has to be found in the data instead of retyped. The shape is stable:
 * three consecutive `button` blocks (WhatsApp, E-Mail, Telefon) preceded by a
 * display paragraph and a heading.
 */
export function contactBlockOf(blocks: Block[]): ContactBlock {
  let start = -1;
  for (let i = 0; i + 2 < blocks.length; i += 1) {
    if (
      blocks[i].type === 'button' &&
      blocks[i + 1].type === 'button' &&
      blocks[i + 2].type === 'button'
    ) {
      start = i;
      break;
    }
  }
  if (start < 0) return { buttons: [], indices: [] };

  const indices: number[] = [start, start + 1, start + 2];
  const buttons = [blocks[start], blocks[start + 1], blocks[start + 2]];

  let text: HeadingBlockData | undefined;
  let title: HeadingBlockData | undefined;
  for (let i = start - 1; i >= 0 && i >= start - 3; i -= 1) {
    const block = blocks[i];
    if (block.type !== 'heading') break;
    const shape = headingShape(block);
    const level = shape.kind !== 'mixed' && /^h[1-6]$/i.test(shape.tag);
    if (!text && !level) {
      text = block;
      indices.unshift(i);
      continue;
    }
    if (!title && level) {
      title = block;
      indices.unshift(i);
      break;
    }
    break;
  }
  return { title, text, buttons, indices };
}
